import { Country } from '@prisma/client';
import { CountryCode } from 'libphonenumber-js';
import { CheckIcon } from 'lucide-react';
import { Checkbox } from '../Checkbox';
import { InputErrorMessage } from '../InputErrorMessage';
import { MultiSelect } from '../MultiSelect';
import { FlagComponent } from '../PhoneInput';
import { SingleCombobox } from '../SingleCombobox';
import { CountryCodeMap, PRIORITY_COUNTRIES } from './countryCodeMap';
import { defaultSupportedCountries } from './supportedCountries';

type CountryInputProps = {
  placeholder?: string;
  error?: string;
  _className?: string;
  disabled?: boolean;
  searchPlaceholder?: string;
  id?: string;
  countriesToNotDisplay?: Country[];
  countries?: Country[];
  readonly?: boolean;
  listClassName?: string;
  // Non-country entries pinned to the top of the list (e.g. a "Global" scope). Each value must not
  // collide with a Country code; these render label-only (no flag).
  additionalOptions?: { label: string; value: string }[];
} & (
  | {
      type: 'single';
      value: string | null;
      onValueChange: (value: string | null) => void;
      disableDeselect?: boolean;
    }
  | {
      type: 'multiple';
      value: string[];
      onValueChange: (value: string[]) => void;
      maxCount?: number;
      disableDeselect?: boolean;
      showSelectAll?: boolean;
      selectAllText?: string;
      modalPopover?: boolean;
    }
);

const CountryInput = (props: CountryInputProps) => {
  const {
    placeholder = 'Select country',
    error,
    type,
    value,
    onValueChange,
    _className,
    disabled,
    id,
    countries = [],
    searchPlaceholder = 'Search option...',
    countriesToNotDisplay = [],
    disableDeselect = false,
    readonly = false,
    listClassName,
    additionalOptions = [],
  } = props;
  const additionalOptionValues = new Set(additionalOptions.map((o) => o.value));
  const maxCount = props.type === 'multiple' ? props.maxCount : undefined;
  const showSelectAll = props.type === 'multiple' ? props.showSelectAll : false;
  const selectAllText =
    props.type === 'multiple' ? props.selectAllText : undefined;
  const modalPopover =
    props.type === 'multiple' ? props.modalPopover : undefined;
  const countryOptions = defaultSupportedCountries
    .filter((key) => key !== Country.None && key !== Country.Other)
    ?.filter((key) => {
      if (!countries.length) return true;
      return countries.includes(key as Country);
    })
    .filter((key) => !countriesToNotDisplay.includes(key as Country))
    .map((ctr) => ({
      label: CountryCodeMap[ctr as Country] ?? '',
      value: ctr,
    }))
    .sort((a, b) => {
      const indexA = PRIORITY_COUNTRIES.indexOf(a.value as Country);
      const indexB = PRIORITY_COUNTRIES.indexOf(b.value as Country);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;

      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      return a.label.localeCompare(b.label);
    })
    .filter((country) => country.label !== '');

  // Pinned non-country entries (e.g. "Global") sit above the sorted countries.
  const combinedOptions = [...additionalOptions, ...countryOptions];

  if (type === 'single')
    return (
      <div className="flex flex-col w-full gap-2">
        <SingleCombobox
          disabled={disabled || readonly}
          notFoundText="No country found."
          error={error}
          options={combinedOptions}
          value={value ?? ''}
          placeholder={placeholder}
          searchPlaceholder={searchPlaceholder}
          id={id}
          canClearSelection={false}
          setValue={(val: string) => {
            if (disableDeselect && val === '') {
              return;
            }
            onValueChange(val);
          }}
          renderTrigger={({ selectedOption }) => (
            <div className="flex justify-between items-center w-full">
              <div className="flex items-center gap-2">
                {selectedOption &&
                additionalOptionValues.has(selectedOption.value) ? null : (
                  <FlagComponent
                    country={selectedOption?.value as CountryCode}
                    countryName={selectedOption?.label ?? ''}
                    _className="size-4 shrink-0"
                  />
                )}
                <span
                  className={`txt-small-regular ${disabled ? 'text-text-disabled cursor-not-allowed' : 'text-text-primary cursor-pointer'}`}
                >
                  {selectedOption?.label}
                </span>
              </div>
            </div>
          )}
          renderOption={({ isSelected, option }) => {
            return (
              <div className="flex px-1.5 justify-between items-center w-full overflow-hidden text-ellipsis">
                <div className="flex items-center gap-2">
                  {additionalOptionValues.has(option.value) ? null : (
                    <FlagComponent
                      country={option.value as CountryCode}
                      countryName={option.label}
                      _className="size-4 shrink-0"
                    />
                  )}
                  <span className="grow txt-small-regular text-text-secondary">
                    {option.label}
                  </span>
                </div>
                {isSelected ? <CheckIcon className="h-4 w-4 shrink-0" /> : null}
              </div>
            );
          }}
          _className={_className}
        />
        {error ? <InputErrorMessage message={error} /> : null}
      </div>
    );

  return (
    <div className="flex flex-col w-full gap-2">
      <MultiSelect
        id={id}
        value={value ?? []}
        defaultValue={value ?? []}
        onValueChange={(selectedCountries: string[]) => {
          if (disableDeselect && selectedCountries.length === 0) {
            return;
          }
          onValueChange(selectedCountries);
        }}
        options={combinedOptions}
        disabled={disabled}
        error={error}
        notFoundText="No country found."
        placeholder={placeholder}
        maxCount={maxCount}
        modalPopover={modalPopover}
        listClassName={listClassName}
        renderOption={({ isSelected, option }) => {
          return (
            <div className="flex justify-between items-center w-full">
              <div className="flex items-center gap-2">
                {isSelected ? (
                  <Checkbox size="sm" checked />
                ) : (
                  <Checkbox size="sm" checked={false} />
                )}
                {additionalOptionValues.has(option.value) ? null : (
                  <FlagComponent
                    country={option.value as CountryCode}
                    countryName={option.label}
                  />
                )}
                <span className="txt-medium-regular text-text-primary">
                  {option.label}
                </span>
              </div>
            </div>
          );
        }}
        hideSelectAll={!showSelectAll}
        selectAllText={selectAllText}
      />
      {error ? <InputErrorMessage message={error} /> : null}
    </div>
  );
};

export { CountryInput, type CountryInputProps };
